import React, { useState, useEffect } from 'react';
import {
  Eye, Activity, TrendingUp, Crosshair, MessageCircle,
  Scissors, Film, Archive, UploadCloud, HardDrive,
  Radio, Layers, Search, Package, Target, Copy,
  PlayCircle, DownloadCloud, ChevronDown, ChevronUp,
  Cpu, Clock, Zap, Database, Shield,
} from 'lucide-react';
import { C } from '../design-system/primitives/constants';
import { CAROLINA_BLUE } from '../../config/uncConstants';

// Icon map from endpoint.icon string to Lucide component
const ICON_MAP = {
  eye: Eye,
  activity: Activity,
  'trending-up': TrendingUp,
  crosshair: Crosshair,
  'message-circle': MessageCircle,
  scissors: Scissors,
  film: Film,
  archive: Archive,
  'upload-cloud': UploadCloud,
  'hard-drive': HardDrive,
  radio: Radio,
  layers: Layers,
  search: Search,
  package: Package,
  target: Target,
  copy: Copy,
  'play-circle': PlayCircle,
  'download-cloud': DownloadCloud,
};

// Category colors — coaching-friendly categories
const CAT_COLORS = {
  'Scouting Intel': '#FF6B35',
  'Player Development': '#4ECDC4',
  'Film Room': '#7B68EE',
  'Analysis Tiers': CAROLINA_BLUE,
  'Player Evaluation': '#3498DB',
  'Film Search': '#9B59B6',
  'Film Upload': '#00CC66',
  'Live Analysis': '#FF4444',
};

function ModelTag({ label, color }) {
  return (
    <span
      className="text-[9px] px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wider"
      style={{ background: (color || C.t3) + '22', color: color || C.t2 }}
    >
      {label}
    </span>
  );
}

function VisionCard({ endpoint }) {
  const [expanded, setExpanded] = useState(false);
  const IconComp = ICON_MAP[endpoint.icon] || Cpu;
  const catColor = CAT_COLORS[endpoint.category] || CAROLINA_BLUE;

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-200 hover:scale-[1.01]"
      style={{
        background: C.s1,
        border: `1px solid ${C.border}`,
      }}
    >
      {/* Header bar */}
      <div className="px-4 py-3 flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: catColor + '1A', border: `1px solid ${catColor}33` }}
        >
          <IconComp size={16} style={{ color: catColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-xs font-semibold truncate" style={{ color: C.text }}>
              {endpoint.name}
            </h4>
            <ModelTag label={endpoint.category} color={catColor} />
          </div>
          <div className="flex items-center gap-3 text-[10px]" style={{ color: C.t3 }}>
            {endpoint.model && (
              <span className="flex items-center gap-1">
                <Cpu size={9} />
                {endpoint.model.replace('gemini-', '').replace('-preview', '')}
              </span>
            )}
            {endpoint.latency && (
              <span className="flex items-center gap-1">
                <Clock size={9} />
                {endpoint.latency}
              </span>
            )}
            {endpoint.inputType && (
              <span className="flex items-center gap-1">
                <Zap size={9} />
                {endpoint.inputType}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Route badge */}
      <div className="px-4 pb-2">
        <code
          className="text-[10px] px-2 py-1 rounded block truncate"
          style={{ background: C.s2, color: CAROLINA_BLUE, fontFamily: 'monospace' }}
        >
          {endpoint.route}
        </code>
      </div>

      {/* Capabilities */}
      <div className="px-4 pb-3 flex flex-wrap gap-1">
        {(endpoint.capabilities || []).slice(0, expanded ? undefined : 3).map((cap) => (
          <span
            key={cap}
            className="text-[9px] px-1.5 py-0.5 rounded"
            style={{ background: C.s2, color: C.t2 }}
          >
            {cap}
          </span>
        ))}
        {!expanded && (endpoint.capabilities || []).length > 3 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ color: C.t3 }}>
            +{endpoint.capabilities.length - 3}
          </span>
        )}
      </div>

      {/* Expand toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2 flex items-center justify-center gap-1 text-[10px] font-medium transition-colors hover:bg-zinc-900"
        style={{ color: C.t3, borderTop: `1px solid ${C.border}` }}
      >
        {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
        {expanded ? 'Collapse' : 'System Prompt'}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3" style={{ borderTop: `1px solid ${C.border}` }}>
          {/* Prompt Summary */}
          <div className="pt-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: C.t3 }}>
              System Prompt Summary
            </p>
            <p className="text-[11px] leading-relaxed" style={{ color: C.t2 }}>
              {endpoint.promptSummary}
            </p>
          </div>

          {/* Tech specs */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg p-2" style={{ background: C.s2 }}>
              <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: C.t3 }}>Input</p>
              <p className="text-[10px] font-medium" style={{ color: C.text }}>{endpoint.inputFormat}</p>
            </div>
            <div className="rounded-lg p-2" style={{ background: C.s2 }}>
              <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: C.t3 }}>Output</p>
              <p className="text-[10px] font-medium" style={{ color: C.text }}>{endpoint.outputFormat}</p>
            </div>
            <div className="rounded-lg p-2" style={{ background: C.s2 }}>
              <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: C.t3 }}>Max Input</p>
              <p className="text-[10px] font-medium" style={{ color: C.text }}>{endpoint.maxInput}</p>
            </div>
            <div className="rounded-lg p-2" style={{ background: C.s2 }}>
              <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: C.t3 }}>Sport</p>
              <p className="text-[10px] font-medium" style={{ color: C.text }}>{endpoint.sport}</p>
            </div>
          </div>

          {/* All capabilities */}
          <div>
            <p className="text-[9px] uppercase tracking-wider mb-1.5" style={{ color: C.t3 }}>Capabilities</p>
            <div className="flex flex-wrap gap-1">
              {(endpoint.capabilities || []).map((cap) => (
                <span
                  key={cap}
                  className="text-[9px] px-1.5 py-0.5 rounded"
                  style={{ background: catColor + '1A', color: catColor }}
                >
                  {cap}
                </span>
              ))}
            </div>
          </div>

          {/* Route file */}
          <div className="flex items-center gap-2 text-[10px]" style={{ color: C.t3 }}>
            <Database size={9} />
            <span>{endpoint.routeFile}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function VisionModelCards() {
  const [endpoints, setEndpoints] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetch('/api/unc/vision-endpoints')
      .then((r) => r.ok ? r.json() : { endpoints: [], categories: [] })
      .then((json) => {
        const data = json.data || json;
        setEndpoints(data.endpoints || []);
        setCategories(data.categories || []);
      })
      .catch(() => {
        setEndpoints([]);
        setCategories([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeCategory === 'all'
    ? endpoints
    : endpoints.filter((e) => e.category === activeCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-5 h-5 rounded-full border-2" style={{ borderColor: C.t3, borderTopColor: CAROLINA_BLUE }} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header stats */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: C.text }}>
            UNC Vision Tools
          </h3>
          <p className="text-[11px] mt-0.5" style={{ color: C.t3 }}>
            {endpoints.length} endpoints across {categories.length} categories
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Shield size={12} style={{ color: '#00CC66' }} />
          <span className="text-[10px] font-medium" style={{ color: '#00CC66' }}>
            gemini-3.1-pro
          </span>
        </div>
      </div>

      {/* Category filter bar */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
        <button
          onClick={() => setActiveCategory('all')}
          className="px-3 py-1.5 rounded-lg text-[10px] font-medium whitespace-nowrap transition-all"
          style={{
            background: activeCategory === 'all' ? CAROLINA_BLUE : C.s2,
            color: activeCategory === 'all' ? '#000' : C.t2,
            border: `1px solid ${activeCategory === 'all' ? CAROLINA_BLUE : C.border}`,
          }}
        >
          All ({endpoints.length})
        </button>
        {categories.map((cat) => {
          const catColor = CAT_COLORS[cat.name] || C.t3;
          const isActive = activeCategory === cat.name;
          return (
            <button
              key={cat.name}
              onClick={() => setActiveCategory(cat.name)}
              className="px-3 py-1.5 rounded-lg text-[10px] font-medium whitespace-nowrap transition-all"
              style={{
                background: isActive ? catColor + '22' : C.s2,
                color: isActive ? catColor : C.t2,
                border: `1px solid ${isActive ? catColor + '55' : C.border}`,
              }}
            >
              {cat.name} ({cat.count})
            </button>
          );
        })}
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((ep) => (
          <VisionCard key={ep.id} endpoint={ep} />
        ))}
      </div>
    </div>
  );
}
